import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, Command, CornerDownLeft } from "lucide-react";
import { navLinks, personalInfo } from "../data/mock";

const extraActions = [
  { label: "Download Resume", href: personalInfo.resumeUrl, external: true, kind: "action" },
  { label: "Open GitHub", href: personalInfo.github, external: true, kind: "action" },
  { label: "Open LinkedIn", href: personalInfo.linkedin, external: true, kind: "action" },
  { label: "Copy Email", href: `mailto:${personalInfo.email}`, kind: "action", copy: personalInfo.email }
];

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);

  // global Cmd/Ctrl + K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const items = useMemo(() => {
    const nav = navLinks.map((n) => ({
      label: `Go to ${n.label}`,
      href: n.href,
      kind: "nav",
      hint: n.hint
    }));
    return [...nav, ...extraActions];
  }, []);

  const filtered = items.filter((i) =>
    i.label.toLowerCase().includes(query.toLowerCase())
  );

  const run = (item) => {
    if (item.copy) {
      navigator.clipboard.writeText(item.copy);
    }
    if (item.external) {
      window.open(item.href, "_blank");
    } else if (item.href) {
      window.location.hash = item.href.replace("#", "");
    }
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[cursor]) run(filtered[cursor]);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.96, y: -10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: -10, opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl rounded-2xl glass-strong shadow-2xl overflow-hidden border border-white/10"
          >
            <div className="flex items-center gap-3 px-4 h-12 border-b border-white/5">
              <Search className="w-4 h-4 text-zinc-500" />
              <input
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setCursor(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="Type a command or search..."
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 font-mono"
              />
              <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] text-zinc-400 font-mono">
                esc
              </kbd>
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2">
              {filtered.length === 0 && (
                <div className="text-center py-8 font-mono text-xs text-zinc-500">
                  no results. try different keywords.
                </div>
              )}
              {filtered.map((item, i) => (
                <button
                  key={item.label}
                  onClick={() => run(item)}
                  onMouseEnter={() => setCursor(i)}
                  className={`w-full flex items-center gap-3 px-3 h-10 rounded-lg text-left transition-colors ${
                    cursor === i ? "bg-cyan-500/10 text-white" : "text-zinc-300 hover:bg-white/5"
                  }`}
                >
                  <span className={`w-6 h-6 rounded-md flex items-center justify-center ${
                    cursor === i ? "bg-cyan-400/20 text-cyan-200" : "bg-white/5 text-zinc-500"
                  }`}>
                    {item.kind === "nav" ? <ArrowRight className="w-3 h-3" /> : <Command className="w-3 h-3" />}
                  </span>
                  <span className="text-sm">{item.label}</span>
                  {item.hint && (
                    <kbd className="ml-auto px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] text-zinc-400 font-mono">
                      {item.hint}
                    </kbd>
                  )}
                  {cursor === i && (
                    <CornerDownLeft className="w-3 h-3 text-cyan-300 ml-2" />
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between px-4 h-9 border-t border-white/5 text-[10px] font-mono text-zinc-500">
              <span>navigate with ↑ ↓, open with ↵</span>
              <span>{filtered.length} results</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
